/**
 * CustomPromptInput — collapsible textarea for teacher-supplied additional
 * instructions appended to every AI-calling endpoint (admission AI, iTarget
 * question generation, report generation, etc.). Backend threads this as
 * `additional_instructions` on the user_message.
 *
 * Optional `surface` + `context` props enable a 🪄 button that calls
 * /api/school/ai/suggest-prompt to populate 3 click-to-insert
 * suggestions — the teacher doesn't have to think the prompt up cold.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../api/school";

interface CustomPromptInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label?: string;
  /** UI surface key — when set, enables the "💡 推荐提示" suggestion button. */
  surface?:
    | "admissions"
    | "itarget"
    | "assignment"
    | "exam"
    | "report_student"
    | "report_class"
    | "extension";
  /** Free-form context passed to the suggestion endpoint. */
  context?: Record<string, unknown>;
}

export function CustomPromptInput({
  value,
  onChange,
  placeholder,
  label,
  surface,
  context,
}: CustomPromptInputProps) {
  const { t, i18n } = useTranslation();
  const effectivePlaceholder = placeholder ?? t("customPromptInput.defaultPlaceholder");
  const effectiveLabel = label ?? t("customPromptInput.defaultLabel");
  const [expanded, setExpanded] = useState(value.trim().length > 0);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function fetchSuggestions() {
    if (!surface) return;
    setErr(null);
    setLoadingSuggest(true);
    try {
      const resp = await fetch("/api/school/ai/suggest-prompt", {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          surface,
          context: context ?? {},
          locale: i18n.language || null,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as { suggestions: string[] };
      setSuggestions(data.suggestions);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingSuggest(false);
    }
  }

  function applySuggestion(s: string) {
    const trimmed = value.trim();
    onChange(trimmed ? `${trimmed}；${s}` : s);
    setSuggestions(null);
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="text-xs text-sky-600 hover:underline"
      >
        {t("customPromptInput.addButton")}
      </button>
    );
  }

  return (
    <label className="block text-sm">
      <span className="flex items-center justify-between mb-1">
        <span className="text-xs text-slate-600">{effectiveLabel}</span>
        {surface && (
          <button
            type="button"
            onClick={fetchSuggestions}
            disabled={loadingSuggest}
            className="text-xs px-2 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
            title={t("customPromptInput.suggestTitle")}
          >
            {loadingSuggest
              ? t("customPromptInput.suggesting")
              : t("customPromptInput.suggestButton")}
          </button>
        )}
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={effectivePlaceholder}
        className="w-full px-2 py-1 rounded border border-slate-200 resize-y text-sm"
      />
      {err && <div className="text-xs text-red-600 mt-1">{err}</div>}
      {suggestions && suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-xs text-slate-500 mr-1 self-center">
            {t("customPromptInput.applyPrompt")}
          </span>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => applySuggestion(s)}
              className="text-xs px-2 py-1 rounded border border-sky-200 bg-white text-sky-700 hover:bg-sky-50 max-w-md text-left"
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          onChange("");
          setSuggestions(null);
          setExpanded(false);
        }}
        className="text-xs text-slate-500 hover:underline mt-1"
      >
        {t("customPromptInput.collapse")}
      </button>
    </label>
  );
}
